import React, { useState, useEffect, useRef } from 'react';
import { adminGetSliders, adminAddSlider, adminUpdateSlider, adminDeleteSlider } from '../../api/admin';
import AdminLayout from './AdminLayout';

const EMPTY_FORM = { id: null, title: '', subtitle: '', button_text: '', button_link: '', sort_order: 0, is_active: 1 };

function AdminSliders() {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    adminGetSliders().then(res => {
      if (res.success) setSliders(res.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd  = () => { setForm(EMPTY_FORM); setImageFile(null); setImagePreview(''); setShowForm(true); setMsg(''); };
  const openEdit = (s) => { setForm({ ...s }); setImageFile(null); setImagePreview(s.image_url || ''); setShowForm(true); setMsg(''); };
  const closeForm = () => { setShowForm(false); setMsg(''); };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.id && !imageFile) { setMsg('Please upload a slider image'); return; }
    setSaving(true); setMsg('');
    try {
      let res;
      if (form.id) {
        res = await adminUpdateSlider({ ...form });
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v !== null) fd.append(k, v); });
        fd.append('image', imageFile);
        res = await adminAddSlider(fd);
      }
      if (res.success) { setMsg('✅ Saved!'); load(); setTimeout(closeForm, 1000); }
      else setMsg('❌ ' + res.message);
    } catch { setMsg('❌ Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete slide "${title || 'Untitled'}"?`)) return;
    await adminDeleteSlider(id);
    load();
  };

  const handleToggle = async (s) => {
    await adminUpdateSlider({ ...s, is_active: s.is_active ? 0 : 1 });
    load();
  };

  return (
    <AdminLayout title="Sliders">
      <div className="adm-page-header">
        <h2>Manage Homepage Sliders</h2>
        <button className="adm-btn-primary" onClick={openAdd}>➕ Add Slide</button>
      </div>

      <p className="adm-hint">💡 Recommended image size: 1920×900px. These images appear in the hero slider on your homepage.</p>

      {showForm && (
        <div className="adm-modal-overlay" onClick={closeForm}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>{form.id ? 'Edit Slide' : 'Add New Slide'}</h3>
              <button onClick={closeForm}>✕</button>
            </div>
            <div className="adm-modal__body">
              {msg && <div className={`adm-msg ${msg.startsWith('✅') ? 'adm-msg--success' : 'adm-msg--error'}`}>{msg}</div>}

              <div className="adm-form-field">
                <label>Title</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Transforming Outdoor Spaces" />
              </div>

              <div className="adm-form-field">
                <label>Subtitle</label>
                <textarea rows={2} value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} placeholder="Short tagline below the title" />
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Button Text</label>
                  <input value={form.button_text} onChange={e => setForm({...form, button_text: e.target.value})} placeholder="e.g. Our Services" />
                </div>
                <div className="adm-form-field">
                  <label>Button Link</label>
                  <input value={form.button_link} onChange={e => setForm({...form, button_link: e.target.value})} placeholder="e.g. /services" />
                </div>
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Sort Order</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} />
                </div>
                <div className="adm-form-field">
                  <label>Status</label>
                  <select value={form.is_active} onChange={e => setForm({...form, is_active: parseInt(e.target.value)})}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="adm-form-field">
                <label>Slider Image {!form.id && '*'}</label>
                <div className="adm-upload-box" onClick={() => fileRef.current.click()}>
                  {imagePreview
                    ? <img src={imagePreview} alt="preview" className="adm-upload-preview" />
                    : <div className="adm-upload-placeholder">📷 Click to upload (1920×900px recommended)</div>
                  }
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImage} />
                {form.id && <p className="adm-field-hint">Image upload not editable here. Delete and re-add to change image.</p>}
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn-secondary" onClick={closeForm}>Cancel</button>
              <button className="adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Slide'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="adm-section">
        {loading ? <div className="adm-loading">Loading...</div> : (
          sliders.length === 0 ? <p className="adm-empty">No slides added yet. Default fallback images will show on homepage.</p> : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr><th>Image</th><th>Title</th><th>Subtitle</th><th>Order</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {sliders.map(s => (
                    <tr key={s.id}>
                      <td><img src={s.image_url} alt={s.title} className="adm-thumb" style={{width:80,height:48}} /></td>
                      <td><strong>{s.title}</strong></td>
                      <td className="adm-truncate">{s.subtitle}</td>
                      <td>{s.sort_order}</td>
                      <td>
                        <button className={`adm-toggle ${s.is_active ? 'adm-toggle--on' : 'adm-toggle--off'}`} onClick={() => handleToggle(s)}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td>
                        <div className="adm-actions">
                          <button className="adm-btn-edit" onClick={() => openEdit(s)}>✏️ Edit</button>
                          <button className="adm-btn-delete" onClick={() => handleDelete(s.id, s.title)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminSliders;